import { useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { guardarConsentimiento, type ConsentimientoAGuardar } from '../../common/services/cuentas.js';

const CLAVE = 'athena-consentimientos-pendientes';
export const CONSENTIMIENTOS_CAMBIADOS = 'athena-consentimientos-cambiados';

interface Pendientes {
  readonly email: string;
  readonly consentimientos: readonly ConsentimientoAGuardar[];
}

/** Solo se aplican al correo que pidió el enlace; en otro navegador se pide aceptación autenticada. */
export function guardarConsentimientosPendientes(email: string, consentimientos: readonly ConsentimientoAGuardar[]): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify({ email: email.trim().toLowerCase(), consentimientos }));
  } catch {
    // Sin almacenamiento, la aceptación se pide después de autenticar.
  }
}

function esConsentimiento(valor: unknown): valor is ConsentimientoAGuardar {
  if (typeof valor !== 'object' || valor === null) return false;
  return (
    'tipo' in valor &&
    (valor.tipo === 'terminos' || valor.tipo === 'newsletter') &&
    'aceptado' in valor &&
    typeof valor.aceptado === 'boolean' &&
    'textoVersion' in valor &&
    typeof valor.textoVersion === 'string' &&
    'texto' in valor &&
    typeof valor.texto === 'string'
  );
}

function esPendiente(valor: unknown): valor is Pendientes {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    'email' in valor &&
    typeof valor.email === 'string' &&
    'consentimientos' in valor &&
    Array.isArray(valor.consentimientos) &&
    valor.consentimientos.every(esConsentimiento)
  );
}

/** Una decisión autenticada posterior sustituye cualquier intento pendiente del mismo tipo. */
export function descartarConsentimientoPendiente(email: string, tipo: ConsentimientoAGuardar['tipo']): void {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (crudo === null) return;
    const valor: unknown = JSON.parse(crudo);
    if (!esPendiente(valor) || valor.email !== email.trim().toLowerCase()) return;
    const restantes = valor.consentimientos.filter(c => c.tipo !== tipo);
    if (restantes.length === 0) localStorage.removeItem(CLAVE);
    else localStorage.setItem(CLAVE, JSON.stringify({ ...valor, consentimientos: restantes }));
  } catch {
    // Un fallo de almacenamiento no invalida la preferencia guardada en el API.
  }
}

async function aplicar(sesion: Session): Promise<void> {
  let crudo: string | null;
  let pendientes: Pendientes;
  try {
    crudo = localStorage.getItem(CLAVE);
    if (crudo === null) return;
    const valor: unknown = JSON.parse(crudo);
    // El formato antiguo no identifica a la persona: nunca se atribuye a la cuenta actual.
    if (!esPendiente(valor)) return;
    pendientes = valor;
  } catch {
    return;
  }
  if (pendientes.email !== sesion.user.email?.trim().toLowerCase()) return;

  const fallidos: ConsentimientoAGuardar[] = [];
  for (const consentimiento of pendientes.consentimientos) {
    // JWT capturado: un cambio de sesión durante el guardado no atribuye aceptación a otra cuenta.
    const guardado = await guardarConsentimiento(consentimiento, sesion.access_token);
    if (!guardado) fallidos.push(consentimiento);
  }
  try {
    // Una solicitud nueva no puede ser borrada por la anterior al acabar.
    if (localStorage.getItem(CLAVE) === crudo) {
      if (fallidos.length === 0) localStorage.removeItem(CLAVE);
      else localStorage.setItem(CLAVE, JSON.stringify({ ...pendientes, consentimientos: fallidos }));
    }
  } catch {
    // El backend conserva lo guardado; no romper la sesión por un fallo de almacenamiento.
  }
  window.dispatchEvent(new Event(CONSENTIMIENTOS_CAMBIADOS));
}

/** Espera al intento de guardado antes de cargar el aviso; fallidos permanecen para la próxima sesión/recarga. */
export function useAplicarConsentimientosPendientes(sesion: Session | null | undefined): boolean {
  const [listo, setListo] = useState<string | null>(null);
  const enCurso = useRef<{ usuarioId: string; promesa: Promise<void> } | null>(null);
  const token = sesion?.access_token;
  useEffect(() => {
    if (sesion === null || sesion === undefined) {
      enCurso.current = null;
      setListo(null);
      return;
    }
    let vigente = true;
    if (enCurso.current?.usuarioId !== sesion.user.id) {
      enCurso.current = { usuarioId: sesion.user.id, promesa: aplicar(sesion) };
    }
    void enCurso.current.promesa.then(() => {
      if (vigente) setListo(sesion.user.id);
    });
    return () => {
      vigente = false;
    };
  }, [token, sesion]);
  return sesion === null || (sesion !== undefined && listo === sesion.user.id);
}
